import { defineStore } from 'pinia';
import { useLocalStorage } from '@vueuse/core';
import { usePage, router } from '@inertiajs/vue3';
import axios from 'axios';
import {
    store as storeIdentity,
    update as updateIdentity,
    logout as anonLogout,
} from '@/routes/anonymous';
import { dashboard } from '@/routes';

const STORAGE_KEY_SESSIONS = 'pokor_sessions';

interface TrackedSession {
    id: string;
    slug: string;
    lastVisited: string;
}

interface AnonymousState {
    token: string | null;
    displayName: string | null;
}

interface AnonymousIdentityResponse {
    anonymous_token: string;
    display_name: string;
}

const trackedSessions = useLocalStorage<TrackedSession[]>(STORAGE_KEY_SESSIONS, []);

export const useAnonymousStore = defineStore('anonymous', {
    state: (): AnonymousState => ({
        token: null,
        displayName: null,
    }),

    getters: {
        hasIdentity: (state): boolean => !!state.token && !!state.displayName,
    },

    actions: {
        hydrate(): void {
            const props = usePage().props;
            this.token = props.auth.anonymous_token ?? null;
            this.displayName = props.auth.anonymous_name ?? null;
        },

        async setIdentity(name: string): Promise<AnonymousIdentityResponse> {
            const { data } = await axios.post<AnonymousIdentityResponse>(storeIdentity.url(), {
                display_name: name,
            });

            this.token = data.anonymous_token;
            this.displayName = data.display_name;

            return data;
        },

        async updateName(name: string): Promise<AnonymousIdentityResponse> {
            if (!this.token) {
                return this.setIdentity(name);
            }

            const { data } = await axios.put<AnonymousIdentityResponse>(updateIdentity.url(), {
                display_name: name,
            });

            this.displayName = data.display_name;

            return data;
        },

        trackSession(sessionId: string, slug: string): void {
            const existing = trackedSessions.value.find((s) => s.id === sessionId);
            if (existing) {
                existing.slug = slug;
                existing.lastVisited = new Date().toISOString();
            } else {
                trackedSessions.value.push({
                    id: sessionId,
                    slug,
                    lastVisited: new Date().toISOString(),
                });
            }
        },

        getTrackedSessions(): TrackedSession[] {
            return trackedSessions.value;
        },

        removeTrackedSession(sessionId: string): void {
            trackedSessions.value = trackedSessions.value.filter((s) => s.id !== sessionId);
        },

        async logout(): Promise<void> {
            await axios.post(anonLogout.url());
            this.token = null;
            this.displayName = null;
            trackedSessions.value = [];
            router.visit(dashboard.url());
        },
    },
});
